import { ReactComponent as EditIcon } from '@assets/icons/edit.svg';
import {
  KeyboardEvent,
  MouseEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import InlineEditActions from './InlineEditActions';
import {
  ActionsAnchor,
  Backdrop,
  EditIconButton,
  EditLayer,
  Form,
  INLINE_EDIT_PENCIL_CLASS,
  MIN_FIELD_WIDTH,
  PortalContainer,
  Root,
  StyledInput,
  Display,
  type InlineEditFieldSize,
} from './styles';

/** Optional pixel nudge applied to the editing field relative to the value box. */
export interface InlineEditFieldPositionOffset {
  top?: number;
  left?: number;
}

export interface InlineEditFieldProps {
  value: string;
  /**
   * Called with the trimmed value when it is non-empty and changed. The field shows it
   * at once; call `revert` if the save fails to put `value` back.
   */
  onCommit: (next: string, revert: () => void) => void;
  /** `false` renders plain text with no pencil. */
  canEdit?: boolean;
  /**
   * The input's `maxLength`, when the server bounds the value. It stops typing past the
   * bound; a longer value already stored still opens whole, and saves only shortened.
   */
  maxLength?: number;
  /** Controls input height / font size. `medium` (default) → 36px input. */
  size?: InlineEditFieldSize;
  /** Applied to the root, so callers can set the typography the value inherits. */
  className?: string;
  /** Render the editing field through a portal to `document.body` (default `true`). */
  portal?: boolean;
  /** Fine-tune the editing field position relative to the value's bounding box. */
  positionOffset?: InlineEditFieldPositionOffset;
  /** Extra width (px) added to the editing field's minimum beyond the value box (default `0`). */
  widthOffset?: number;
  /** Notified when edit mode opens (`true`) or closes (`false`). */
  onEditingChange?: (editing: boolean) => void;
  /** Prefix of the pencil / input / portal test ids (`<prefix>-edit`, …). */
  testIdPrefix?: string;
  'data-testid'?: string;
}

/** Where the portalled field opens, and the bounds its text-driven width stays within. */
type FieldBox = { top: number; left: number; minWidth: number; maxWidth: number };

/** Room kept between the portalled field and the edge it may grow to. */
const EDGE_GUTTER = 8;

/** An `overflow` that clips: any clipping axis also makes the horizontal one clip. */
const CLIPPING_OVERFLOW = /hidden|clip|auto|scroll/;

/**
 * The right edge the field may grow to: the nearest ancestor that clips its overflow
 * (the row card, a scroll pane), else the viewport less a classic scrollbar.
 */
const growthEdge = (el: HTMLElement): number => {
  const viewportEdge = document.documentElement.clientWidth;
  for (let node = el.parentElement; node && node !== document.body; node = node.parentElement) {
    if (CLIPPING_OVERFLOW.test(getComputedStyle(node).overflow)) {
      return Math.min(viewportEdge, node.getBoundingClientRect().right);
    }
  }
  return viewportEdge;
};

/**
 * Inline-editable text with a hover pencil affordance — Figma `inline-edit-input`
 * (Type=Title) as the `document-row` rename state draws it. Presentational: the caller
 * decides who may edit and persists the value in `onCommit`.
 *
 * - The **value itself is inert** — clicks pass through to the parent (e.g. a row
 *   `<Link>` opens the document). Only the pencil enters edit mode.
 * - While editing, the field renders in a portal positioned over the value
 *   (adjustable via `positionOffset` / `widthOffset`). It is as wide as the text it
 *   holds, up to the row's edge, so the start is never cut off.
 * - Because React events bubble through the *component* tree (not the DOM tree)
 *   out of a portal, the edit layer **stops propagation** of pointer/keyboard
 *   events so the parent block never receives a stray click while editing.
 */
export default function InlineEditField({
  value,
  onCommit,
  canEdit = true,
  maxLength,
  size = 'medium',
  className,
  portal = true,
  positionOffset,
  widthOffset = 0,
  onEditingChange,
  testIdPrefix = 'inline-edit-field',
  'data-testid': dataTestId,
}: InlineEditFieldProps) {
  const { t } = useTranslation('dashboard', { keyPrefix: 'global' });
  const rootRef = useRef<HTMLSpanElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const pencilRef = useRef<HTMLButtonElement>(null);
  const wasEditingRef = useRef(false);

  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [fieldBox, setFieldBox] = useState<FieldBox>({
    top: 0,
    left: 0,
    minWidth: MIN_FIELD_WIDTH,
    maxWidth: MIN_FIELD_WIDTH,
  });

  // Keep the displayed/draft value in sync when the persisted value changes.
  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    onEditingChange?.(isEditing);
  }, [isEditing, onEditingChange]);

  const measure = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const left = rect.left + (positionOffset?.left ?? 0);
    const maxWidth = Math.max(MIN_FIELD_WIDTH, growthEdge(el) - left - EDGE_GUTTER);
    setFieldBox({
      top: rect.top + window.scrollY + (positionOffset?.top ?? 0) - (size === 'small' ? 8 : 0),
      left: left + window.scrollX,
      minWidth: Math.min(Math.max(MIN_FIELD_WIDTH, rect.width + widthOffset), maxWidth),
      maxWidth,
    });
  }, [positionOffset?.top, positionOffset?.left, widthOffset, size]);

  // Re-measure synchronously before paint so the field opens exactly over the value.
  useLayoutEffect(() => {
    if (isEditing) measure();
  }, [isEditing, measure]);

  // Focus once the field is mounted.
  useEffect(() => {
    if (!isEditing) return;
    const frame = requestAnimationFrame(() => {
      const input = inputRef.current;
      if (!input) return;
      input.focus();
      // Caret at the end of the value instead of selecting all text — unless the field
      // can't show the whole value, where the caret goes to the start so its beginning
      // stays in view.
      const fits = input.scrollWidth <= input.clientWidth;
      const caret = fits ? input.value.length : 0;
      input.setSelectionRange(caret, caret);
      if (!fits) input.scrollLeft = 0;
    });
    return () => cancelAnimationFrame(frame);
  }, [isEditing]);

  // Back to the pencil once the field closes: the field and its ✓/✕ unmount, which would
  // otherwise drop keyboard and screen-reader users at the top of the page.
  useEffect(() => {
    if (wasEditingRef.current && !isEditing) pencilRef.current?.focus();
    wasEditingRef.current = isEditing;
  }, [isEditing]);

  const stop = (e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Isolate the input's pointer events from the parent row WITHOUT
  // `preventDefault` — that keeps native selection (click, drag-to-select,
  // double-click word) working while the form still shields the row. The form's
  // `stop` handlers only run for the surrounding chrome, never the input.
  const stopPropagationOnly = (e: SyntheticEvent) => {
    e.stopPropagation();
  };

  const openEditor = (e: MouseEvent) => {
    stop(e);
    if (!canEdit) return;
    setDraft(value);
    setIsEditing(true);
  };

  const closeEditor = () => setIsEditing(false);

  const commit = useCallback(() => {
    const next = draft.trim();
    // Empty or unchanged → just close without calling the owner.
    if (next && next !== value) {
      onCommit(next, () => setDraft(value));
    } else {
      setDraft(value);
    }
    closeEditor();
  }, [draft, value, onCommit]);

  const cancel = useCallback(() => {
    setDraft(value);
    closeEditor();
  }, [value]);

  const handleSubmit = (e: SyntheticEvent) => {
    stop(e);
    commit();
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    }
  };

  const editLayer = (
    <Form
      onSubmit={handleSubmit}
      onClick={stop}
      onMouseDown={stop}
      onPointerDown={stop}
      onKeyDown={handleKeyDown}
    >
      <StyledInput
        inputRef={inputRef}
        $size={size}
        value={draft}
        data-value={draft}
        onChange={(e) => setDraft(e.target.value)}
        inputProps={{
          'aria-label': t('name'),
          'data-testid': `${testIdPrefix}-input`,
          maxLength,
          // No intrinsic width of its own: the hidden copy of the value sizes the field.
          size: 1,
          // Stop propagation only (no preventDefault) so native text selection —
          // including click-and-drag over part of the value — keeps working.
          onClick: stopPropagationOnly,
          onDoubleClick: stopPropagationOnly,
          onMouseDown: stopPropagationOnly,
          onPointerDown: stopPropagationOnly,
        }}
      />
      <ActionsAnchor>
        <InlineEditActions
          onCancel={(e) => {
            stop(e);
            cancel();
          }}
          onConfirm={(e) => {
            stop(e);
            commit();
          }}
        />
      </ActionsAnchor>
    </Form>
  );

  const isInlineEditing = canEdit && isEditing && !portal;
  const isPortalEditing = canEdit && isEditing && portal;

  return (
    <Root ref={rootRef} className={className} data-testid={dataTestId}>
      {isInlineEditing ? (
        <EditLayer>{editLayer}</EditLayer>
      ) : (
        <>
          {/* While the portal field is open it's shown on top of the value, so
              the static value is hidden (but kept in layout to measure against). */}
          <Display $size={size} $editable={canEdit} $hidden={isPortalEditing} title={draft}>
            {draft}
          </Display>

          {canEdit && !isPortalEditing && (
            <EditIconButton
              ref={pencilRef}
              size='small'
              type='button'
              className={INLINE_EDIT_PENCIL_CLASS}
              aria-label={t('rename')}
              data-testid={`${testIdPrefix}-edit`}
              onClick={openEditor}
              onMouseDown={stop}
            >
              <EditIcon width={16} height={16} aria-hidden />
            </EditIconButton>
          )}
        </>
      )}

      {isPortalEditing &&
        ReactDOM.createPortal(
          <>
            <Backdrop onClick={handleSubmit} onMouseDown={stop} />
            <PortalContainer style={fieldBox} data-testid={`${testIdPrefix}-portal`}>
              {editLayer}
            </PortalContainer>
          </>,
          document.body,
        )}
    </Root>
  );
}
